import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraBadge, SpecraButton, SpecraCard, SpecraEmptyState, SpecraHeading, SpecraInlineBanner, SpecraKeyValueRow, SpecraLink, SpecraListPage, SpecraPageHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraSwimlaneTimeline, SpecraText, SpecraUnverifiedClaim, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { entityHref } from '../Capture/record/entityRoutes';
import { growthCrumbs } from '../GrowthHub/growthCrumbs';
import { GROWTH_THREAD_FLAG } from '../GrowthHub/growthFlags';
import { ClaimAudienceControl } from '../Registries/hearsay/ClaimAudienceControl';
import { registryKeys, useIntelClaimsForLinks } from '../Registries/hearsay/hooks';
import { claimAttribution } from '../Registries/hearsay/IntelClaimsRailSection';
import { useLandscapeSeats, useListSeats } from './hooks/useLandscapeSeats';
import { useLandscape } from './hooks/useLandscapes';
import { buildSeatTimeline, claimsBySeat, SEAT_LENSES, seatHearsayLinks } from './seats/buildSeatTimeline';
import { ManageSeatsDrawer } from './seats/ManageSeatsDrawer';
import { monthYear, toYear, yearOf } from './seats/seatTime';
import { whatChanged } from './seats/whatChanged';
import { yearHolders, yearPosition } from './seats/yearPosition';
const LENS_LABEL = {
    everything: 'Everything',
    ours: 'Ours',
    competitors: 'Competitors',
    people: 'People'
};
const KIND_BADGE = {
    market: {
        label: 'Market',
        variant: 'info'
    },
    work: {
        label: 'Workspace',
        variant: 'violet'
    },
    decision: {
        label: 'Decision',
        variant: 'warning'
    },
    delivery: {
        label: 'Delivery',
        variant: 'teal'
    },
    theirs: {
        label: 'Competitor',
        variant: 'warning'
    },
    people: {
        label: 'People',
        variant: 'info'
    },
    loss: {
        label: 'Outcome',
        variant: 'neutral'
    },
    lesson: {
        label: 'Lesson',
        variant: 'neutral'
    }
};
const ENTITY_NOUN = {
    pursuit: 'thread',
    program: 'program',
    opportunity: 'notice',
    notice: 'notice',
    rfi_response: 'RFI response',
    proposal: 'proposal'
};
/** GRW-25: with growth_thread_v1 off this is a redirect and issues no RPC. */ export function LandscapeSeatsPage() {
    const { id = '' } = useParams();
    const ready = useFeatureFlagsReady();
    const on = useFeatureFlag(GROWTH_THREAD_FLAG) === true;
    if (!ready) return null;
    if (!on) return /*#__PURE__*/ _jsx(Navigate, {
        to: `/landscapes/${id}`,
        replace: true
    });
    return /*#__PURE__*/ _jsx(SeatHistory, {
        id: id
    }, id);
}
function SeatHistory({ id }) {
    const [params, setParams] = useSearchParams();
    const lensParam = params.get('lens');
    const lens = SEAT_LENSES.includes(lensParam) ? lensParam : 'everything';
    const setLens = (next)=>setParams((prev)=>{
            const p = new URLSearchParams(prev);
            if (next === 'everything') p.delete('lens');
            else p.set('lens', next);
            return p;
        }, {
            replace: true
        });
    const navigate = useNavigate();
    const qc = useQueryClient();
    const seatsQ = useLandscapeSeats(id);
    const landscape = useLandscape(id).data;
    const resp = seatsQ.data;
    const seats = resp?.seats ?? [];
    const looksHidden = !!resp && seats.length === 0 && !resp.canEdit && resp.marketTotal === 0;
    const listQ = useListSeats({
        landscapeId: id
    }, looksHidden);
    const hearsayQ = useIntelClaimsForLinks(useMemo(()=>seatHearsayLinks(seats), [
        seats
    ]), seats.length > 0);
    const claims = hearsayQ.data?.claims;
    const hearsay = useMemo(()=>claimsBySeat(seats, claims ?? []), [
        seats,
        claims
    ]);
    const now = useMemo(()=>new Date(), []);
    const [stationId, setStationId] = useState();
    const [claimId, setClaimId] = useState();
    const [periodId, setPeriodId] = useState();
    const [managing, setManaging] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    const name = landscape?.name ?? 'Landscape';
    useRecordBreadcrumbs(growthCrumbs({
        label: 'Landscapes',
        href: '/landscapes'
    }, {
        label: name,
        href: `/landscapes/${id}`
    }, {
        label: 'Seat history'
    }));
    const customers = [
        ...new Set(seats.map((s)=>s.customerLabel).filter(Boolean))
    ];
    const customer = customers.length === 1 ? customers[0] : 'the government';
    const year = Number((periodId ?? `y${now.getUTCFullYear()}`).slice(1));
    const events = useMemo(()=>seats.flatMap((s)=>s.events.map((e)=>({
                    seatName: s.name,
                    event: e
                }))), [
        seats
    ]);
    const yearEvents = events.filter((x)=>yearOf(x.event.occurredAt) === year).sort((a, b)=>a.event.occurredAt.localeCompare(b.event.occurredAt));
    const picked = events.find((x)=>x.event.id === stationId)?.event;
    const fallback = [
        ...yearEvents
    ].reverse().find((x)=>x.event.kind === 'decision')?.event;
    const shown = claimId ? undefined : picked ?? fallback;
    const shownClaim = claimId ? claims?.find((c)=>c.id === claimId) : undefined;
    const selectStation = (sid)=>{
        setClaimId(undefined);
        setStationId(sid);
        const y = yearOf(events.find((x)=>x.event.id === sid)?.event.occurredAt ?? '');
        if (y !== null) setPeriodId(`y${y}`);
    };
    const timeline = useMemo(()=>resp && seats.length > 0 ? buildSeatTimeline(resp, now, lens, {
            stationId: claimId ? undefined : stationId ?? shown?.id,
            periodId
        }, {
            hearsay,
            onActivateClaim: (cid)=>{
                setStationId(undefined);
                setClaimId(cid);
            }
        }) : undefined, [
        resp,
        seats.length,
        now,
        lens,
        stationId,
        claimId,
        shown?.id,
        periodId,
        hearsay
    ]);
    const header = /*#__PURE__*/ _jsx(SpecraPageHeader, {
        eyebrow: "Landscape · seat history",
        titleSize: "home",
        title: name,
        description: `Each row is a seat: a need ${customer} keeps buying. Bars are the contracts written for it, coloured by who held them. Above each bar are our pursuits for that seat. Seats split, merge, and change hands; the rows show how.`,
        actions: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Page",
                    value: "seats",
                    options: [
                        {
                            value: 'overview',
                            label: 'Overview'
                        },
                        {
                            value: 'seats',
                            label: 'Seat history'
                        }
                    ],
                    onValueChange: (v)=>{
                        if (v === 'overview') navigate(`/landscapes/${id}`);
                    },
                    testId: Testids.landscapeSeats.viewToggle
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: `/landscapes/${id}/briefs`,
                    className: specraButtonVariants({
                        variant: 'secondary'
                    }),
                    children: "Review briefs"
                }),
                resp?.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: ()=>setManaging(true),
                    "data-testid": Testids.landscapeSeats.manageButton,
                    children: "Manage seats"
                }) : null
            ]
        })
    });
    let body;
    if (seatsQ.isError) {
        body = /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            tone: "danger",
            variant: "framed",
            "data-testid": Testids.landscapeSeats.error,
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                size: "sm",
                "data-testid": Testids.landscapeSeats.retry,
                onClick: ()=>seatsQ.refetch(),
                children: "Retry"
            }),
            children: "Seat history could not be loaded."
        });
    } else if (!resp || looksHidden && listQ.isLoading) {
        body = /*#__PURE__*/ _jsx(SpecraText, {
            tone: "muted",
            children: "Loading…"
        });
    } else if (looksHidden && listQ.data?.growthHidden) {
        body = /*#__PURE__*/ _jsx(SpecraEmptyState, {
            "data-testid": Testids.landscapeSeats.growthOnly,
            title: "Seat history is available to Growth"
        });
    } else if (seats.length === 0) {
        body = /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                resp.marketTotal > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: [
                        "Market · ",
                        resp.marketTotal,
                        " notices"
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": Testids.landscapeSeats.empty,
                    title: "No seats yet",
                    description: `A seat is a need ${customer} keeps buying. Add the seats in this market and their contract rounds; our programs can be suggested for you.`,
                    action: resp.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>setManaging(true),
                        children: "Manage seats"
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        tone: "muted",
                        children: "Ask a Growth lead to add seats."
                    })
                })
            ]
        });
    } else {
        const rounds = seats.flatMap((s)=>s.rounds);
        const noFeed = !rounds.some((r)=>r.registryContractId) && !rounds.some((r)=>r.holderKind === 'competitor' || r.status === 'prior');
        const notices = (resp.market ?? []).filter((m)=>yearOf(m.month) === year).reduce((n, m)=>n + m.count, 0);
        const changes = whatChanged(seats);
        body = /*#__PURE__*/ _jsxs("div", {
            className: "space-y-6",
            children: [
                noFeed && !bannerDismissed ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    variant: "framed",
                    "data-testid": Testids.landscapeSeats.noAwardFeed,
                    onDismiss: ()=>setBannerDismissed(true),
                    children: "Competitor contract history isn't imported yet. Add past rounds by hand in Manage seats."
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Lens",
                            value: lens,
                            options: SEAT_LENSES.map((l)=>({
                                    value: l,
                                    label: LENS_LABEL[l]
                                })),
                            onValueChange: setLens,
                            testId: Testids.landscapeSeats.lens
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            children: "Click a year or any marker"
                        })
                    ]
                }),
                timeline ? /*#__PURE__*/ _jsx(SpecraSwimlaneTimeline, {
                    ...timeline,
                    "data-testid": Testids.landscapeSeats.timeline,
                    renderLink: renderRouterLink,
                    onPeriodSelect: setPeriodId,
                    onStationSelect: selectStation
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid gap-4 md:grid-cols-2",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            "data-testid": Testids.landscapeSeats.yearCard,
                            className: "space-y-3 p-4",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: `${year} in this market`,
                                    action: /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: [
                                            notices,
                                            " notices"
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    variant: "fact",
                                    label: "Our position",
                                    value: yearPosition(seats, year) || '—'
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    variant: "fact",
                                    label: "Who held the seats",
                                    value: yearHolders(seats, year) || '—'
                                }),
                                yearEvents.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    "data-testid": Testids.landscapeSeats.yearEmpty,
                                    title: `Nothing recorded in ${year}`
                                }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                                    items: yearEvents.map(({ seatName, event })=>({
                                            id: event.id,
                                            leading: /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "meta",
                                                tone: "muted",
                                                children: monthYear(toYear(event.occurredAt) ?? year).slice(0, 3)
                                            }),
                                            label: event.title,
                                            description: seatName,
                                            actions: /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "secondary",
                                                size: "xs",
                                                onClick: ()=>selectStation(event.id),
                                                children: "Show"
                                            })
                                        })),
                                    trailing: null
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraCard, {
                            "data-testid": Testids.landscapeSeats.selectedCard,
                            className: "space-y-2 p-4",
                            children: shownClaim ? /*#__PURE__*/ _jsx(SpecraUnverifiedClaim, {
                                kindLabel: "Hearsay",
                                tone: "warning",
                                claim: `“${shownClaim.statement}”`,
                                attribution: claimAttribution(shownClaim) || undefined,
                                confidence: shownClaim.confidence.toLowerCase(),
                                verified: shownClaim.verification === 'CORROBORATED',
                                actions: /*#__PURE__*/ _jsx(ClaimAudienceControl, {
                                    claim: shownClaim,
                                    size: "xs",
                                    "data-testid": Testids.landscapeSeats.claimAudience,
                                    onChanged: ()=>{
                                        void qc.invalidateQueries({
                                            queryKey: registryKeys.all
                                        });
                                        void qc.invalidateQueries({
                                            queryKey: [
                                                'landscape-seats'
                                            ]
                                        });
                                    }
                                })
                            }) : shown ? /*#__PURE__*/ _jsx(SelectedEvent, {
                                event: shown
                            }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                "data-testid": Testids.landscapeSeats.selectedEmpty,
                                title: "Pick a bar or a marker"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    "data-testid": Testids.landscapeSeats.whatChanged,
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: "What changed",
                            action: /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: "Drafted from the seats; edit in Compose report"
                            })
                        }),
                        changes.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                            tone: "muted",
                            children: "Nothing has changed yet."
                        }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                            trailing: null,
                            items: changes.map((c)=>({
                                    id: c.id,
                                    label: c.text,
                                    actions: c.link ? /*#__PURE__*/ _jsx(SpecraLink, {
                                        href: c.link.href,
                                        renderLink: renderRouterLink,
                                        children: c.link.label
                                    }) : undefined
                                }))
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.landscapeSeats.root,
        "data-landscape-id": id,
        children: [
            managing && resp?.canEdit && /*#__PURE__*/ _jsx(ManageSeatsDrawer, {
                landscapeId: id,
                landscapeName: name,
                seats: seats,
                canEdit: resp.canEdit,
                onClose: ()=>setManaging(false)
            }),
            /*#__PURE__*/ _jsx(SpecraListPage, {
                padding: "page",
                header: header,
                children: body
            })
        ]
    });
}
function SelectedEvent({ event }) {
    const badge = KIND_BADGE[event.kind] ?? {
        label: event.kind,
        variant: 'neutral'
    };
    const href = entityHref(event.entityKind, event.entityId);
    const t = toYear(event.occurredAt);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: badge.variant,
                        children: badge.label
                    }),
                    t !== null ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: monthYear(t)
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraHeading, {
                size: "sm",
                children: event.title
            }),
            event.description ? /*#__PURE__*/ _jsx(SpecraText, {
                children: event.description
            }) : null,
            event.sourceLabel ? /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                children: [
                    "Source: ",
                    event.sourceLabel
                ]
            }) : null,
            href ? /*#__PURE__*/ _jsxs(SpecraLink, {
                href: href,
                renderLink: renderRouterLink,
                children: [
                    "Open ",
                    ENTITY_NOUN[event.entityKind] ?? event.entityKind,
                    " →"
                ]
            }) : null
        ]
    });
}
